import {
  OrganizationList,
  SignIn,
  SignUp,
  TaskChooseOrganization,
  TaskResetPassword,
  TaskSetupMFA,
  useAuth,
  useClerk,
  useOrganization,
  useSession,
  useUser,
} from "@clerk/react";
import { api } from "@clarent/backend/convex/_generated/api";
import { Link, Outlet, useRouterState, type LinkProps } from "@tanstack/react-router";
import { AuthLoading, Authenticated, Unauthenticated, useMutation, useQuery } from "convex/react";
import { useEffect, useState, type ReactNode } from "react";
import { errorMessage } from "../lib/convexError";
import { currentDestination } from "../lib/destination";
import { initials } from "../lib/format";
import { LoginPage, SsoCallback } from "../routes/LoginPage";
import { FeedbackDialog } from "./FeedbackDialog";
import { RecordingNotice } from "./RecordingNotice";
import { BellIcon, BoardIcon, CalendarIcon, ChatIcon, ClockIcon, MenuIcon, UserIcon } from "./icons";
import { Logo } from "./Logo";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";

export function AppShell() {
  return (
    <>
      <AuthLoading>
        <FullPage>Signing you in…</FullPage>
      </AuthLoading>
      <Unauthenticated>
        <SignedOut />
      </Unauthenticated>
      <Authenticated>
        <OrgGate>
          <Shell>
            <Outlet />
          </Shell>
        </OrgGate>
      </Authenticated>
    </>
  );
}

/**
 * Decision D36: what a signed-out URL shows. Convex counts a session that still has a task to
 * finish (the Clerk instances force choosing a workspace) as signed out. That case comes first:
 * the sign-in page can't help someone who already has a session. Invitation links carry Clerk's
 * `__clerk_ticket`, which Clerk's own forms accept. Everything else gets the sign-in page.
 */
function SignedOut() {
  const { session } = useSession();
  // Re-render on navigation; the URL itself is read from window.location below.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (session?.status === "pending") return <FinishSignIn session={session} />;
  const params = new URLSearchParams(window.location.search);
  if (params.has("__clerk_ticket") && params.get("__clerk_status") !== "complete") {
    return <AcceptInvitation newAccount={params.get("__clerk_status") === "sign_up"} />;
  }
  return pathname === "/sso-callback" ? <SsoCallback /> : <LoginPage />;
}

function AuthScreen({ title, children, footer }: { title: string; children: ReactNode; footer?: ReactNode }) {
  return (
    <main className="flex min-h-screen flex-col items-center gap-6 bg-bg px-4 py-10">
      <Logo size={30} wordmark="text-[22px] leading-7" />
      <h1 className="page-title text-center">{title}</h1>
      {children}
      {footer}
    </main>
  );
}

/** A signed-in session with a task left, usually choosing or creating a workspace. */
function FinishSignIn({ session }: { session: NonNullable<ReturnType<typeof useSession>["session"]> }) {
  const clerk = useClerk();
  const task = session.currentTask?.key;
  const destination = currentDestination();
  const email = session.user?.primaryEmailAddress?.emailAddress;
  // Clerk's task screens carry their own "Signed in as … Sign out" footer; OrganizationList doesn't.
  const knownTask = task === "reset-password" || task === "setup-mfa" || task === "choose-organization";
  return (
    <AuthScreen
      title={task === "reset-password" ? "Set a new password" : task === "setup-mfa" ? "Set up two-step sign-in" : "Choose your workspace"}
      footer={
        knownTask ? null : (
          <p className="text-[13px] text-muted">
            {email ? <>Signed in as {email}. </> : null}
            <button type="button" className="font-bold text-brand-dark hover:underline" onClick={() => clerk.signOut()}>
              Sign out
            </button>
          </p>
        )
      }
    >
      {task === "reset-password" ? (
        <TaskResetPassword redirectUrlComplete={destination} />
      ) : task === "setup-mfa" ? (
        <TaskSetupMFA redirectUrlComplete={destination} />
      ) : task === "choose-organization" ? (
        <TaskChooseOrganization redirectUrlComplete={destination} />
      ) : (
        <OrganizationList hidePersonal afterSelectOrganizationUrl={destination} afterCreateOrganizationUrl={destination} />
      )}
    </AuthScreen>
  );
}

/** Invitation emails link here with a ticket. Clerk's forms accept it and add the person to the workspace. */
function AcceptInvitation({ newAccount }: { newAccount: boolean }) {
  const destination = currentDestination();
  return (
    <AuthScreen title="Join your team on Backplan">
      {newAccount ? (
        <SignUp routing="hash" forceRedirectUrl={destination} signInForceRedirectUrl={destination} />
      ) : (
        <SignIn routing="hash" forceRedirectUrl={destination} signUpForceRedirectUrl={destination} />
      )}
    </AuthScreen>
  );
}

function FullPage({ children }: { children: ReactNode }) {
  return <main className="flex min-h-screen flex-col items-center justify-center p-6 text-sm text-muted">{children}</main>;
}

/** Requires an active Clerk organisation, then makes sure Convex knows about it. */
function OrgGate({ children }: { children: ReactNode }) {
  const { orgId } = useAuth();
  if (!orgId) {
    return (
      <FullPage>
        <div className="mb-6 text-center">
          <h1 className="page-title">Choose your organisation</h1>
          <p className="mt-1 text-muted">Each machine shop is an organisation. Create one or pick one you belong to.</p>
        </div>
        <OrganizationList hidePersonal afterSelectOrganizationUrl="/board" afterCreateOrganizationUrl="/board" />
      </FullPage>
    );
  }
  return <EnsureOrganisation key={orgId}>{children}</EnsureOrganisation>;
}

function EnsureOrganisation({ children }: { children: ReactNode }) {
  const { organization } = useOrganization();
  const ensure = useMutation(api.organisations.ensure);
  const [state, setState] = useState<{ status: "pending" } | { status: "ready" } | { status: "error"; message: string }>({ status: "pending" });

  const orgName = organization?.name;
  useEffect(() => {
    if (!orgName) return;
    let cancelled = false;
    ensure({ name: orgName })
      .then(() => !cancelled && setState({ status: "ready" }))
      .catch((e: unknown) => !cancelled && setState({ status: "error", message: errorMessage(e) }));
    return () => {
      cancelled = true;
    };
  }, [ensure, orgName]);

  if (state.status === "error") {
    return (
      <FullPage>
        <div className="panel max-w-md p-6">
          <h2 className="app-title text-ink">Couldn't set up your organisation</h2>
          <p className="mt-2">{state.message}</p>
          <p className="mt-3 text-xs">If this keeps happening, see "Clerk setup" in the README.</p>
        </div>
      </FullPage>
    );
  }
  if (state.status === "pending") return <FullPage>Loading your shop…</FullPage>;
  return <>{children}</>;
}

/**
 * Decision D33: the sidebar from the design's Account screen. On narrower screens it slides in
 * from a top bar. Sidebar items with no page yet (Jobs, Customers, Outside processors, Team,
 * Help and feedback, Invite a colleague) are left out. Board and Calendar carry their own page
 * header with New job (design system v2.1), so the shell only adds one on the other pages.
 * Help and feedback opens a dialog (D45).
 */
function Shell({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => setMenuOpen(false), [pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);
  const ownHeader = /^\/(board|calendar|jobs)(\/|$)/.test(pathname);

  return (
    <div className="min-h-screen lg:flex">
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-line bg-bg px-[18px] py-3 lg:hidden">
        <button type="button" aria-label="Open menu" className="-ml-1 rounded-md p-1 text-ink hover:bg-surface" onClick={() => setMenuOpen(true)}>
          <MenuIcon />
        </button>
        <Logo size={24} wordmark="text-base leading-5" />
        <Link to="/jobs/new" className="btn-primary ml-auto">
          + New job
        </Link>
      </header>
      {menuOpen && <div className="fixed inset-0 z-30 bg-ink/30 lg:hidden" onClick={() => setMenuOpen(false)} />}
      <Sidebar className={menuOpen ? "fixed inset-y-0 left-0 z-40 flex shadow-float" : "hidden lg:sticky lg:top-0 lg:flex lg:h-screen"} />
      <div className="min-w-0 flex-1">
        {!ownHeader && (
          <div className="hidden justify-end px-7 pt-6 lg:flex">
            <Link to="/jobs/new" className="btn-primary px-[18px] py-2.5 text-sm">
              + New job
            </Link>
          </div>
        )}
        <main className={`px-[18px] py-6 lg:px-7 lg:pb-16 ${ownHeader ? "" : "lg:pt-4"}`}>{children}</main>
      </div>
    </div>
  );
}

function Sidebar({ className }: { className: string }) {
  const alerts = useQuery(api.alerts.listPending);
  const pending = alerts?.length ?? 0;
  const { user } = useUser();
  const name = user?.fullName || user?.primaryEmailAddress?.emailAddress || "Your account";
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  return (
    <aside className={`w-[264px] shrink-0 flex-col gap-5 overflow-y-auto border-r border-line bg-bg px-4 py-5 ${className}`}>
      <Link to="/board" className="px-2 py-1">
        <Logo />
      </Link>
      <WorkspaceSwitcher />
      <nav className="flex flex-col gap-0.5">
        {/* A job's drawer and modal open over the board, so Board stays lit under /jobs. */}
        <NavItem to="/board" icon={<BoardIcon />} alsoActive={/^\/jobs(\/|$)/}>
          Board
        </NavItem>
        <NavItem to="/calendar" icon={<CalendarIcon />}>
          Calendar
        </NavItem>
        <NavItem to="/alerts" icon={<BellIcon />}>
          Alerts
          {pending > 0 && (
            <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-risk px-1.5 text-[11px] leading-[14px] font-bold text-white">{pending}</span>
          )}
        </NavItem>
      </nav>
      <nav className="flex flex-col gap-0.5 border-t border-line pt-3.5" aria-label="Set-up">
        <span className="px-3 pb-1.5 font-mono text-[10px] leading-3 font-bold tracking-[0.06em] text-subtle">SET-UP</span>
        <NavItem to="/settings" icon={<ClockIcon />}>
          Shop week
        </NavItem>
        <NavItem to="/account" icon={<UserIcon />}>
          Your account
        </NavItem>
      </nav>
      <div className="flex-1" />
      {/* Optional tip card: only shortcuts that exist. */}
      <div className="flex flex-col gap-2 rounded-[14px] bg-brand-tint p-3.5">
        <span className="text-xs leading-4 font-extrabold text-brand-dark">In a job</span>
        <span className="flex items-center gap-2 text-xs leading-4 font-semibold">
          <span className="flex gap-[3px]">
            <kbd className="rounded-[6px] bg-bg px-[7px] py-[3px] font-mono text-[11px] leading-[14px] font-bold">Alt</kbd>
            <kbd className="rounded-[6px] bg-bg px-[7px] py-[3px] font-mono text-[11px] leading-[14px] font-bold">M</kbd>
          </span>
          Add a machining step
        </span>
      </div>
      <div className="flex flex-col gap-0.5 border-t border-line pt-3.5">
        <button
          type="button"
          onClick={() => setFeedbackOpen(true)}
          className="flex items-center gap-3 rounded-[10px] px-3 py-[9px] text-left text-sm leading-[18px] font-medium text-ink transition-colors hover:bg-surface"
        >
          <ChatIcon />
          Help and feedback
        </button>
        <Link to="/account" className="flex items-center gap-3 rounded-[10px] px-3 py-[9px] text-sm leading-[18px] font-medium hover:bg-surface">
          <span className="heading flex size-[22px] shrink-0 items-center justify-center rounded-full bg-brand-tint text-[9px] leading-3 text-brand-dark">
            {initials(name)}
          </span>
          <span className="truncate">{name}</span>
        </Link>
      </div>
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
      <RecordingNotice />
    </aside>
  );
}

function NavItem({ to, icon, alsoActive, children }: { to: LinkProps["to"]; icon: ReactNode; alsoActive?: RegExp; children: ReactNode }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const forced = alsoActive?.test(pathname) ?? false;
  const active = "bg-brand-tint font-bold text-brand-dark";
  const inactive = "font-medium text-ink hover:bg-surface";
  return (
    <Link
      to={to}
      className="flex items-center gap-3 rounded-[10px] px-3 py-[9px] text-sm leading-[18px] transition-colors"
      activeProps={{ className: active }}
      inactiveProps={{ className: forced ? active : inactive }}
    >
      {icon}
      {children}
    </Link>
  );
}
